import React from 'react';
import classnames from 'classnames';
import Translate, { useI18n } from '@hydra/common/components/i18n';
import getTranslatedUrl from '@hydra/utils/getTranslatedUrl';
import { sendEvent } from '@hydra/utils/gtm';
import Collapse from '@material-ui/core/Collapse';
import Chip from '@material-ui/core/Chip';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Icon from '@hydra/common/components/Icon';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListSubheader from '@material-ui/core/ListSubheader';
import Link from '@material-ui/core/Link';
import Divider from '@material-ui/core/Divider';
import { Item, MenuItem } from '@hydra/common/app/Header/menuItems';

const useStyles = makeStyles((theme) => ({
  header: {
    pointerEvents: 'none',
    opacity: '1 !important', // Avoid opacity when element is disabled
    '& > span': {
      fontWeight: theme.typography.fontWeightBold,
      fontSize: theme.spacing(1.375),
      color: theme.palette.secondary.main,
      textTransform: 'uppercase'
    },
    '&:focus, &:hover, &:active': {
      textDecoration: 'none'
    },
    textTransform: 'uppercase',
    padding: theme.spacing(0, 2)
  },
  item: {
    padding: theme.spacing(1, 2),
    color: theme.palette.secondary.dark,
    fontSize: theme.spacing(2.375)
  },
  itemText: {
    display: 'inline-flex',
    fontSize: theme.spacing(2.375)
  },
  groupItem: {
    padding: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between'
  },
  activeGroupItem: {
    backgroundColor: (theme.palette.grey as any).smoke,
    '&:hover': {
      backgroundColor: (theme.palette.grey as any).smoke
    }
  },
  groupContainer: {
    border: `1px solid ${(theme.palette.grey as any).smoke}`,
    borderTop: 'none'
  },
  chip: {
    marginLeft: theme.spacing(1),
    fontSize: theme.typography.fontSizeSmall,
    height: theme.spacing(2.25),
    alignSelf: 'center'
  },
  helpDropdownButton: {
    fontWeight: theme.typography.fontWeightRegular,
    color: theme.palette.secondary.dark
  },
  divider: {
    backgroundColor: (theme.palette.grey as any).smoke
  }
}));

interface INestedListProps {
  menuOpen: string | null;
  setMenuOpen: React.Dispatch<React.SetStateAction<string | null>>;
  itemKey: string;
  contentColor: 'white' | 'gray';
  menuItems?: Item[][];
  menu: MenuItem;
}

export default function NestedList ({
  menuOpen,
  setMenuOpen,
  itemKey,
  contentColor,
  menuItems,
  menu
}: INestedListProps) {
  const classes = useStyles();
  const { locale } = useI18n();

  function handleGtmClick (label = '') {
    if (label) {
      sendEvent(label);
    }
  }

  function handleClick () {
    if (menuOpen === itemKey) {
      setMenuOpen(null);
    } else {
      setMenuOpen(itemKey);
    }
  }

  return (
    <>
      <ListItem button onClick={handleClick} className={classnames(classes.groupItem, { [classes.activeGroupItem]: menuOpen === itemKey })}>
        <Typography noWrap className={classes.helpDropdownButton}>
          <Translate content={menu.label} />
        </Typography>
        {menuOpen === itemKey
          ? (
            <Icon type='chevron-up' color={contentColor} />
            )
          : (
            <Icon type='chevron-down' color={contentColor} />
            )}
      </ListItem>
      <Collapse in={menuOpen === itemKey} timeout='auto' unmountOnExit>
        <List component='div' disablePadding className={classes.groupContainer}>
          {menuItems && menuItems.map((items: Item[], gIndex: number) => (
            <List disablePadding key={gIndex}>
              {items.map((item: Item, index: number) => item.header
                ? (
                  <div key={index}>
                    {gIndex !== 0 && <Divider className={classes.divider} />}
                    <ListSubheader component='div' className={classes.header}>
                      <Translate content={item.label} />
                    </ListSubheader>
                  </div>
                  )
                : (
                  <ListItem
                    key={index}
                    href={
                      item.externalLink
                        ? item.externalLink
                        : item.link && getTranslatedUrl(item.link, locale)
                    }
                    button
                    component={Link}
                    onClick={() => {
                      handleGtmClick(item.gtmLabel);
                    }}
                    className={classes.item}
                  >
                    <Typography noWrap component='span' className={classes.itemText}>
                      <Translate content={item.label} />
                      {item.chipTag && (
                        <Chip
                          label={<Translate content={item.chipTag} />}
                          className={classes.chip}
                          color='primary'
                          size='small'
                        />
                      )}
                    </Typography>
                  </ListItem>
                  )
              )}
            </List>
          ))}
        </List>
      </Collapse>
    </>
  );
}
